/**
 * Nexus Agent Service
 *
 * DB read/write for nexus_relationship_proposals — emergent relationship
 * candidates from Nexus, custom relationship agents, the cross-pool sweep and
 * the semantic import paths. Every proposal is written through
 * propose_item_relationship (migration 941), from the browser and the server
 * runtime alike. The agent's settings live in agent_settings (agent_type
 * 'nexus'; DEP-038 retired nexus_agent_config).
 */

import { getSupabaseClient } from './supabaseClient';


// ── Types ─────────────────────────────────────────────────────────────────────

// ── Queries ───────────────────────────────────────────────────────────────────

export interface NexusRelationshipProposal {
  id: string;
  workspaceId: string;
  sourceItemId: string;
  targetItemId: string;
  relationshipType: string;
  relationshipSubtype?: string;
  directionality: 'directed' | 'undirected';
  sourceItemRole?: 'source' | 'target';
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
  status: 'active' | 'accepted' | 'dismissed';
  proposedByAgent: string;
  createdAt: string;
  reviewedAt?: string;
  reviewedByUserId?: string;
}

function rowToProposal(row: Record<string, unknown>): NexusRelationshipProposal {
  return {
    id:                   row.id as string,
    workspaceId:          row.workspace_id as string,
    sourceItemId:         row.source_item_id as string,
    targetItemId:         row.target_item_id as string,
    relationshipType:     row.relationship_type as string,
    relationshipSubtype:  (row.relationship_subtype as string | null) ?? undefined,
    directionality:       (row.directionality as 'directed' | 'undirected') ?? 'undirected',
    sourceItemRole:       (row.source_item_role as 'source' | 'target' | null) ?? undefined,
    rationale:            (row.rationale as string) ?? '',
    confidence:           (row.confidence as 'high' | 'medium' | 'low') ?? 'medium',
    status:               row.status as 'active' | 'accepted' | 'dismissed',
    proposedByAgent:      (row.proposed_by_agent as string) ?? 'nexus',
    createdAt:            row.created_at as string,
    reviewedAt:           (row.reviewed_at as string | null) ?? undefined,
    reviewedByUserId:     (row.reviewed_by_user_id as string | null) ?? undefined,
  };
}

// ── Proposal queries ──────────────────────────────────────────────────────────

export async function getNexusProposals(
  workspaceId: string,
  status: 'active' | 'accepted' | 'dismissed' = 'active',
  limit = 200,
): Promise<{ data: NexusRelationshipProposal[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('nexus_relationship_proposals')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('status', status)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) return { data: [], error: error.message };
    return {
      data: (data as Record<string, unknown>[]).map(rowToProposal),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function getNexusProposalHistory(
  workspaceId: string,
  limit = 50,
): Promise<{ data: NexusRelationshipProposal[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('nexus_relationship_proposals')
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('status', ['accepted', 'dismissed'])
      .order('reviewed_at', { ascending: false })
      .limit(limit);

    if (error) return { data: [], error: error.message };
    return {
      data: (data as Record<string, unknown>[]).map(rowToProposal),
      error: null,
    };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Proposal mutations ────────────────────────────────────────────────────────

export interface CreateNexusProposalInput {
  workspaceId: string;
  sourceItemId: string;
  targetItemId: string;
  relationshipType: string;
  relationshipSubtype?: string;
  directionality: 'directed' | 'undirected';
  sourceItemRole?: 'source' | 'target';
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
  /**
   * Proposing agent recorded on the row. Defaults to 'nexus'. The semantic
   * excursion return path routes spatially-inferred relationships through
   * this same review lifecycle as 'excursion_return'.
   */
  proposedByAgent?: string;
}

export async function createNexusProposal(
  input: CreateNexusProposalInput,
): Promise<{ data: NexusRelationshipProposal | null; existing?: boolean; error: string | null }> {
  try {
    const { data, error } = await getSupabaseClient().rpc('propose_item_relationship', {
      p_workspace_id:         input.workspaceId,
      p_agent_type:           input.proposedByAgent ?? 'nexus',
      p_source_item_id:       input.sourceItemId,
      p_target_item_id:       input.targetItemId,
      p_relationship_type:    input.relationshipType,
      p_relationship_subtype: input.relationshipSubtype ?? null,
      p_directionality:       input.directionality,
      p_source_item_role:     input.sourceItemRole ?? null,
      p_rationale:            input.rationale,
      p_confidence:           input.confidence,
    });
    if (error) return { data: null, error: error.message };
    const result = (data ?? {}) as { success?: boolean; existing?: boolean; error?: string; proposal?: Record<string, unknown> };
    if (result.success !== true || !result.proposal) return { data: null, error: result.error ?? 'The relationship could not be proposed.' };
    return { data: rowToProposal(result.proposal), existing: result.existing === true, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function acceptNexusProposal(
  proposalId: string,
  userId: string,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc('accept_nexus_proposal', {
      p_proposal_id: proposalId,
      p_user_id:     userId,
    });
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export async function dismissNexusProposal(
  proposalId: string,
  userId: string,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc('dismiss_nexus_proposal', {
      p_proposal_id: proposalId,
      p_user_id:     userId,
    });
    return { error: error?.message ?? null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
